import {
  useState,
  Fragment,
} from 'react';
import {
  navigate,
} from 'vike/client/router';
import {
  usePageContext,
} from 'vike-react/usePageContext';
import {
  useAlerts,
  useWishlists,
  useAuthentication,
} from '@dinosave/core/stores';
import type {
  WishlistEntity,
} from '@dinosave/core/types';
import ConfirmationModal from '@/components/Modals/ConfirmationModal';
import {
  AUTH,
} from '@/constants/namespaces';
import useModal from '@/hooks/useModal';
import Button from '@/ui/components/Button';
import Text from '@/ui/components/Text';
import {
  TrashIcon,
} from '@/ui/icons';

import styles from './WishlistDelete.module.css';

type WishlistDeleteProps = {
  wishlist: WishlistEntity;
  onSuccess?: () => void;
}

const WishlistDelete = (props: WishlistDeleteProps) => {
  // ##########################################################################
  // PARAMS
  // ##########################################################################

  const {
    routeParams,
  } = usePageContext();
  const {
    wishlistSlug,
  } = routeParams;

  // ##########################################################################
  // PROPS
  // ##########################################################################

  const {
    wishlist,
    onSuccess,
  } = props;

  // ##########################################################################
  // STORES
  // ##########################################################################

  const authenticatedUser = useAuthentication(s => s.authenticatedUser);
  const authenticatedUserSlug = authenticatedUser?.slug;

  const deleteUserWishlist = useWishlists(AUTH, s => s.deleteUserWishlist);

  const addSuccess = useAlerts(s => s.addSuccess);

  // ##########################################################################
  // STATE
  // ##########################################################################

  const [loading, setLoading] = useState(false);

  const confirmationModal = useModal();

  // ##########################################################################
  // FUNCTIONS
  // ##########################################################################

  const handleDelete = async () => {
    if (
      !authenticatedUser
    ) return;

    try {
      setLoading(true);
      await deleteUserWishlist(authenticatedUserSlug, wishlist);

      addSuccess({
        title: 'Wishlist deleted',
        message: wishlist.name,
      });

      if (
        onSuccess
      ) onSuccess();

      if (
        wishlistSlug
      ) navigate(`/u/${authenticatedUserSlug}`);
    } finally {
      setLoading(false);
    }
  };

  // ##########################################################################
  // RENDER
  // ##########################################################################

  return (
    <Fragment>
      <ConfirmationModal
        {...confirmationModal}
        loading={loading}
        onCancel={confirmationModal.close}
        onConfirm={handleDelete}
      >
        <Text
          kind="paragraph"
          size="regular"
        >
          Your&nbsp;
          <Text weight="600">
            &quot;{wishlist.name}&quot;
          </Text>
          &nbsp;wishlist will be permanently deleted
          {!!wishlist.itemsCount && (
            <Fragment>
              &nbsp;along with&nbsp;
              <Text weight="600">
                {wishlist.itemsCount}&nbsp;
                {wishlist.itemsCount > 1 ? 'items ' : 'item '}
              </Text>
              in it
            </Fragment>
          )}.
        </Text>
      </ConfirmationModal>

      <div className={styles.wishlistDelete}>
        <Text
          kind="caption"
          size="large"
        >
          Your wishlist will be permanently deleted
          {!!wishlist.itemsCount && (
            <Fragment>
              &nbsp;along with&nbsp;
              <Text weight="600">
                {wishlist.itemsCount}&nbsp;
                {wishlist.itemsCount > 1 ? 'items' : 'item'}
              </Text>
              &nbsp;in it
            </Fragment>
          )}
          .
        </Text>

        <div className={styles.button}>
          <Button
            color="secondary"
            onClick={confirmationModal.toggle}
            centered
          >
            <TrashIcon viewBox="1 2.75 20 20" />

            <div className={styles.buttonText}>
              <Text
                kind="control"
                size="small"
                color="danger"
              >
                Delete
              </Text>
            </div>
          </Button>
        </div>
      </div>
    </Fragment>
  );
};

export default WishlistDelete;
